import { create } from 'zustand';
import { createSelectors } from '../utils/createSelectors';
import { User } from '../types/user';
import { authApi } from '../api/authApi';
import { usersApi, type UserUpdateProfileRequest } from '../api/usersApi';
import { refreshAccessToken } from '../api/tokenRefresh';
import { tokenStorage } from '../utils/tokenStorage';
import {
  AsyncState,
  INITIAL_ASYNC_STATE,
  createAsyncHelpers,
} from '../utils/asyncState';
import { extractLocaleFromPath, isSupportedLocale } from '../utils/i18n';
import { mapApiUserToUser } from '@/utils/userMapper';
import { navigateToPath } from '@/lib/navigation';
import i18n from '@/i18n';
import { toast } from 'sonner';

// Utility function to clear wizard data from localStorage
const clearWizardData = () => {
  try {
    localStorage.removeItem('wizard-storage');
    console.info('Wizard data cleared from localStorage');
  } catch (error) {
    console.warn('Failed to clear wizard data:', error);
  }
};

const getLandingPath = () => {
  if (typeof window === 'undefined') return null;

  const pathLocale = extractLocaleFromPath(window.location.pathname);
  const language = i18n.resolvedLanguage || i18n.language;
  const languageLocale = language?.split('-')[0].toLowerCase();
  const locale = pathLocale || (languageLocale && isSupportedLocale(languageLocale) ? languageLocale : undefined);

  return locale ? `/${locale}` : '/';
};

type AuthStore = AsyncState & {
  user: User | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setInitializing: (initializing: boolean) => void;
  initializeAuth: () => Promise<void>;
  refreshAuth: () => Promise<boolean>;
  updateProfile: (updates: UserUpdateProfileRequest) => Promise<void>;
  clearProviderData: (
    provider: 'google' | 'facebook' | 'spotify' | 'tidal',
  ) => Promise<void>;
  logout: () => Promise<void>;
  deleteAccount: (reason?: string) => Promise<void>;
  reset: () => void;
  silentRefresh: () => Promise<boolean>;
  handleSessionExpired: () => void;
};

// Profile fetch deduplication cache
let profileFetchPromise: Promise<User> | null = null;

// RefreshAuth deduplication cache
let refreshAuthPromise: Promise<boolean> | null = null;

/**
 * Fetches user profile with deduplication
 * Prevents multiple simultaneous requests to the same endpoint
 */
const fetchUserProfile = async (): Promise<User> => {
  // If there's already a request in progress, return the existing promise
  if (profileFetchPromise) {
    console.info('Profile fetch: Using existing promise (deduplication)');
    return profileFetchPromise;
  }

  // Create a new request
  profileFetchPromise = usersApi.getMe().then(mapApiUserToUser);

  try {
    const result = await profileFetchPromise;
    return result;
  } catch (error) {
    console.error('Profile fetch: Failed', error);
    throw error;
  } finally {
    // Clear the promise cache after a small delay to handle rapid sequential calls
    setTimeout(() => {
      profileFetchPromise = null;
    }, 100);
  }
};

const useAuthStoreBase = create<AuthStore>((set, get) => {
  const asyncHelpers = createAsyncHelpers(set);

  return {
    user: null,
    ...INITIAL_ASYNC_STATE,
    loading: true, // Override initial state for auth
    isAuthenticated: false,
    isInitializing: true,

    setUser: (user) =>
      set({
        user,
        isAuthenticated: !!user,
        loading: false,
        error: null,
      }),

    setLoading: asyncHelpers.setLoading,
    setError: asyncHelpers.setError,
    setInitializing: (initializing) => set({ isInitializing: initializing }),

    initializeAuth: async () => {
      set({ isInitializing: true, error: null });

      try {
        // Check if we have stored tokens
        const tokens = tokenStorage.getTokens();
        if (!tokens) {
          // No tokens found - user is guest, keep wizard data for wizard workflow
          set({
            isInitializing: false,
            loading: false,
            isAuthenticated: false,
            user: null,
          });
          return;
        }

        // Check if refresh token is expired
        if (tokenStorage.isRefreshTokenExpired()) {
          tokenStorage.clearAll();
          // Refresh token expired - don't clear wizard data, let wizard handle expiration
          set({
            isInitializing: false,
            loading: false,
            isAuthenticated: false,
            user: null,
          });
          return;
        }

        // Validate authentication using refresh token if needed
        const isValid = await get().refreshAuth();
        if (!isValid) {
          // Refresh failed - wizard data already cleared in refreshAuth/logout if needed
          set({
            isInitializing: false,
            loading: false,
            isAuthenticated: false,
            user: null,
          });
          return;
        }

        // If we reach here, authentication is valid and profile is loaded
        set({
          isInitializing: false,
          loading: false,
          error: null,
        });
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Clear tokens on critical initialization failure
        tokenStorage.clearAll();
        // Don't clear wizard data - let user retry auth with their data intact
        set({
          isInitializing: false,
          loading: false,
          isAuthenticated: false,
          user: null,
          error: i18n.t('auth.errors.initializationFailed'),
        });
      }
    },

    refreshAuth: async () => {
      // If there's already a refresh in progress, return the existing promise
      if (refreshAuthPromise) {
        console.info('RefreshAuth: Using existing promise (deduplication)');
        return refreshAuthPromise;
      }

      // Create a new refresh promise
      refreshAuthPromise = (async () => {
        let attemptedRefreshToken: string | null = null;
        try {
          const tokens = tokenStorage.getTokens();
          attemptedRefreshToken = tokens?.refreshToken ?? null;
          if (!tokens?.refreshToken) {
            return false;
          }

          // If access token exists and is not expired, prefer profile validation
          // before attempting refresh to avoid unnecessary refresh calls right
          // after successful login/verification.
          const accessToken = tokenStorage.getAccessToken();
          const isAccessTokenExpired = tokenStorage.isAccessTokenExpired();
          if (accessToken && !isAccessTokenExpired) {
            try {
              const user = await fetchUserProfile();
              set({
                isAuthenticated: true,
                user,
                error: null,
              });
              return true;
            } catch (profileError) {
              // A thrown UNAUTHORIZED means makeRequest already attempted a
              // refresh via the shared coordinator and it (or the retry) was
              // still rejected — the session is dead. Bail out instead of firing
              // a second, redundant refresh here.
              if (
                profileError instanceof Error &&
                profileError.message === 'UNAUTHORIZED'
              ) {
                return false;
              }
              console.warn(
                'Profile fetch with current access token failed, trying refresh:',
                profileError,
              );
            }
          }

          // Check if refresh token is expired
          if (tokenStorage.isRefreshTokenExpired()) {
            tokenStorage.clearAll();
            return false;
          }

          // Attempt to refresh access token via the shared, deduplicated
          // coordinator so we don't race other refresh paths on the rotating
          // single-use refresh token.
          const refreshResult = await refreshAccessToken();
          if (!refreshResult.ok) {
            return false;
          }

          // Fetch updated user profile with deduplication
          try {
            const user = await fetchUserProfile();
            set({
              isAuthenticated: true,
              user,
              error: null,
            });
            return true;
          } catch (profileError) {
            console.error(
              'Failed to fetch profile after token refresh:',
              profileError,
            );
            // Don't logout on profile fetch failure, user is still authenticated
            return true;
          }
        } catch (error) {
          console.error('Token refresh failed:', error);

          const latestRefreshToken = tokenStorage.getRefreshToken();
          if (
            attemptedRefreshToken &&
            latestRefreshToken &&
            latestRefreshToken !== attemptedRefreshToken
          ) {
            try {
              const user = await fetchUserProfile();
              set({
                isAuthenticated: true,
                user,
                error: null,
              });
              return true;
            } catch (profileError) {
              console.error(
                'Profile reload failed after token rotation:',
                profileError,
              );
              return !!tokenStorage.getAccessToken();
            }
          }

          // Clear wizard data before logout to prevent data poisoning
          clearWizardData();
          get().logout();
          return false;
        } finally {
          // Clear the promise cache after a small delay
          setTimeout(() => {
            refreshAuthPromise = null;
          }, 100);
        }
      })();

      return refreshAuthPromise;
    },

    updateProfile: async (updates) => {
      set({ loading: true, error: null });
      try {
        const updatedProfile = await usersApi.updateMe(updates);
        const mappedUser = mapApiUserToUser(updatedProfile);
        set({
          user: mappedUser,
          loading: false,
          error: null,
        });
      } catch (error) {
        console.error('[auth] Profile update error:', error);
        set({
          loading: false,
          error: i18n.t('auth.errors.profileUpdateFailed'),
        });
        throw error;
      }
    },

    clearProviderData: async (provider) => {
      set({ loading: true, error: null });
      try {
        const currentUser = get().user;
        if (!currentUser) {
          throw new Error('User not found');
        }

        // Create new metadata object to clear provider-specific data
        const newMetadata = { ...currentUser.metadata };

        // Remove provider-specific metadata keys
        delete newMetadata.extra;

        // Mark provider as disconnected in metadata
        newMetadata[`${provider}_disconnected_at`] = new Date().toISOString();

        const updates: UserUpdateProfileRequest = {
          metadata: newMetadata,
        };

        const updatedProfile = await usersApi.updateMe(updates);
        const mappedUser = mapApiUserToUser(updatedProfile);
        set({
          user: mappedUser,
          loading: false,
          error: null,
        });
      } catch (error) {
        set({
          loading: false,
          error:
            error instanceof Error
              ? error.message
              : 'Provider data deletion failed',
        });
        throw error;
      }
    },

    logout: async () => {
      set({ loading: true, error: null });
      try {
        await authApi.logout();
        // Clear wizard data to prevent data poisoning
        clearWizardData();
        // Clear user state after successful logout
        set({
          user: null,
          isAuthenticated: false,
          loading: false,
          error: null,
        });
        // Show success toast
        toast.success(i18n.t('auth.success.logoutSuccess'));
        const landingPath = getLandingPath();
        if (landingPath) {
          navigateToPath(landingPath);
        }
      } catch (error) {
        console.error('[auth] Logout error:', error);
        set({
          loading: false,
          error: i18n.t('auth.errors.logoutFailed'),
        });
        throw error;
      }
    },

    deleteAccount: async (reason?: string) => {
      set({ loading: true, error: null });
      try {
        await authApi.deleteAccount({
          confirmation: 'DELETE' as const,
          reason,
        });
        // Clear wizard data to prevent data poisoning
        clearWizardData();
        // Clear user state after successful deletion
        set({
          user: null,
          isAuthenticated: false,
          loading: false,
          error: null,
        });
      } catch (error) {
        console.error('[auth] Account deletion error:', error);
        set({
          loading: false,
          error: i18n.t('auth.errors.accountDeletionFailed'),
        });
        throw error;
      }
    },

    reset: () => {
      // Clear wizard data when resetting auth state
      clearWizardData();
      set({
        user: null,
        ...INITIAL_ASYNC_STATE,
        loading: true, // Override for auth reset
        isAuthenticated: false,
        isInitializing: true,
      });
    },

    // Silent refresh - used for periodic token refresh without affecting UI
    silentRefresh: async () => {
      try {
        const tokens = tokenStorage.getTokens();
        if (!tokens || !get().isAuthenticated) {
          return false;
        }

        // Only refresh if token is close to expiring
        if (!tokenStorage.isAccessTokenExpired()) {
          return true;
        }

        // Attempt refresh via the shared, deduplicated coordinator.
        const result = await refreshAccessToken();
        if (result.ok) {
          return true;
        }

        // The refresh token is definitively dead (backend rejected it).
        // Reconcile UI state so the header stops showing a logged-in user
        // for a session that no longer works. Transient network errors
        // (authRejected === false) are left alone for a later retry.
        if (result.authRejected) {
          get().handleSessionExpired();
        }
        return false;
      } catch (error) {
        console.error('Silent refresh failed:', error);
        return false;
      }
    },

    // Reconcile local auth state with reality when the backend has definitively
    // rejected the session. Unlike logout(), this makes no network call and only
    // clears client state — route guards and the header react to `user`/`isAuthenticated`.
    handleSessionExpired: () => {
      if (!get().isAuthenticated && !get().user) {
        return;
      }
      tokenStorage.clearAll();
      clearWizardData();
      set({
        user: null,
        isAuthenticated: false,
        loading: false,
        error: null,
        isInitializing: false,
      });
      toast.info(i18n.t('auth.errors.sessionExpired'));
    },
  };
});

export const useAuthStore = createSelectors(useAuthStoreBase);
